<template>
  <platform-modal
    v-model="showModal"
    icon="history"
    :title="modalTitle"
    :width="800"
    hide-save
    :content-card-padded="false"
    @close="$emit('close')"
  >
    <div v-if="batch" class="d-flex flex-column">
      <v-tabs
        v-model="tab"
        color="primary"
        class="px-3 shrink"
      >
        <v-tab>
          {{ $t('form.import.history.created') }} ({{ createdCount }})
        </v-tab>
        <v-tab>
          {{ $t('form.import.history.failed') }} ({{ failedCount }})
        </v-tab>
        <v-tab>
          {{ $t('form.import.history.manuallyCompleted') }} ({{ manuallyCompletedCount }})
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="grow pt-3">
          <v-tab-item>
            <div class="pa-4">
              <div v-if="createdCount === 0" class="text-center text--secondary py-4">
                {{ $t('form.import.history.noItems') }}
              </div>
              <template v-else>
                <div v-if="createdCompanies.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.companies') }} ({{ createdCompanies.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(company, index) in createdCompanies" :key="'c-' + index">
                          <td>{{ company.name || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="createdIndividuals.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.individuals') }} ({{ createdIndividuals.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.individual.preferredName') }}</th>
                          <th>{{ $t('form.individual.role') }}</th>
                          <th>{{ $t('form.individual.email') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(individual, index) in createdIndividuals" :key="'i-' + index">
                          <td>{{ individual.preferredName || '-' }}</td>
                          <td>{{ individual.role ? $t(`shared.individual.roles.${individual.role}`) : '-' }}</td>
                          <td>{{ individual.email || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="createdCommittees.length > 0">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.committees') }} ({{ createdCommittees.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                          <th>{{ $t('form.import.history.members') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(committee, index) in createdCommittees" :key="'cm-' + index">
                          <td>{{ committee.name || '-' }}</td>
                          <td>{{ getCommitteeMembers(committee) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </template>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="pa-4">
              <div v-if="failedCount === 0" class="text-center text--secondary py-4">
                {{ $t('form.import.history.noItems') }}
              </div>
              <template v-else>
                <div v-if="failedCompanies.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.companies') }} ({{ failedCompanies.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(company, index) in failedCompanies" :key="'fc-' + index">
                          <td>{{ company.name || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="failedIndividuals.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.individuals') }} ({{ failedIndividuals.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.individual.preferredName') }}</th>
                          <th>{{ $t('form.individual.role') }}</th>
                          <th>{{ $t('form.individual.email') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(individual, index) in failedIndividuals" :key="'fi-' + index">
                          <td>{{ individual.preferredName || '-' }}</td>
                          <td>{{ individual.role ? $t(`shared.individual.roles.${individual.role}`) : '-' }}</td>
                          <td>{{ individual.email || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="failedCommittees.length > 0">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.committees') }} ({{ failedCommittees.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                          <th>{{ $t('form.import.history.members') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(committee, index) in failedCommittees" :key="'fcm-' + index">
                          <td>{{ committee.name || '-' }}</td>
                          <td>{{ failedCommitteeMembers(committee) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </template>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="pa-4">
              <div v-if="manuallyCompletedCount === 0" class="text-center text--secondary py-4">
                {{ $t('form.import.history.noItems') }}
              </div>
              <template v-else>
                <div v-if="manuallyCompletedCompanies.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.companies') }} ({{ manuallyCompletedCompanies.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(company, index) in manuallyCompletedCompanies" :key="'mc-' + index">
                          <td>{{ company.name || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="manuallyCompletedIndividuals.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.individuals') }} ({{ manuallyCompletedIndividuals.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.individual.preferredName') }}</th>
                          <th>{{ $t('form.individual.role') }}</th>
                          <th>{{ $t('form.individual.email') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(individual, index) in manuallyCompletedIndividuals" :key="'mi-' + index">
                          <td>{{ individual.preferredName || '-' }}</td>
                          <td>{{ individual.role ? $t(`shared.individual.roles.${individual.role}`) : '-' }}</td>
                          <td>{{ individual.email || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="manuallyCompletedCommittees.length > 0">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.committees') }} ({{ manuallyCompletedCommittees.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                          <th>{{ $t('form.import.history.members') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(committee, index) in manuallyCompletedCommittees" :key="'mcm-' + index">
                          <td>{{ committee.name || '-' }}</td>
                          <td>{{ getCommitteeMembers(committee) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </template>
            </div>
          </v-tab-item>
        </v-tabs-items>
    </div>
  </platform-modal>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import dayjs from 'dayjs'
import { Company } from '@/API'
import API from '@/services/API'
import { listFullCompanyIndividuals } from '@/graphql/custom/listFullCompanyIndividuals'
import {
  ImportBatchFailedCommitteeRow,
  ImportBatchFailedCompanyRow,
  ImportBatchFailedIndividualRow,
  PlatformImportBatch
} from '@betterboards/shared/types/Import'
import { PlatformCompanyIndividual, PlatformIndividual } from '@betterboards/shared/types/Individual'
import { PlatformCommittee } from '@betterboards/shared/types/Committee'
import PlatformModal from '@/components/PlatformModal.vue'

export default Vue.extend({
  name: 'ImportHistoryModal',
  components: {
    PlatformModal
  },
  props: {
    batch: {
      type: Object as PropType<PlatformImportBatch | null>,
      default: null
    }
  },
  data () {
    return {
      showModal: true,
      tab: 0,
      historyIndividuals: [] as PlatformIndividual[]
    }
  },
  computed: {
    selectedCompany (): Company | null {
      return this.$store.state.Company.selectedCompany
    },
    committeesList (): PlatformCommittee[] {
      return this.$store.getters['Committee/list'] ?? []
    },
    modalTitle (): string {
      const baseTitle = this.$t('form.import.history.detailsTitle') as string
      if (this.batch?.createdAt) {
        const formattedDate = dayjs(this.batch.createdAt).format('DD MMM YYYY')
        return `${baseTitle} - ${formattedDate}`
      }
      return baseTitle
    },
    createdCompanies (): Company[] {
      return this.getCompanies(this.storedIds(this.batch?.created?.company))
    },
    createdIndividuals (): PlatformIndividual[] {
      return this.getIndividuals(this.storedIds(this.batch?.created?.individuals))
    },
    createdCommittees (): PlatformCommittee[] {
      return this.getCommittees(this.storedIds(this.batch?.created?.committees))
    },
    failedCompanies (): ImportBatchFailedCompanyRow[] {
      return this.batch?.failed?.company ?? []
    },
    failedIndividuals (): ImportBatchFailedIndividualRow[] {
      return this.batch?.failed?.individuals ?? []
    },
    failedCommittees (): ImportBatchFailedCommitteeRow[] {
      return this.batch?.failed?.committees ?? []
    },
    manuallyCompletedCompanies (): Company[] {
      return this.getCompanies(this.storedIds(this.batch?.manuallyCompleted?.company))
    },
    manuallyCompletedIndividuals (): PlatformIndividual[] {
      return this.getIndividuals(this.storedIds(this.batch?.manuallyCompleted?.individuals))
    },
    manuallyCompletedCommittees (): PlatformCommittee[] {
      return this.getCommittees(this.storedIds(this.batch?.manuallyCompleted?.committees))
    },
    createdCount (): number {
      return this.createdCompanies.length + this.createdIndividuals.length + this.createdCommittees.length
    },
    failedCount (): number {
      return this.failedCompanies.length + this.failedIndividuals.length + this.failedCommittees.length
    },
    manuallyCompletedCount (): number {
      return this.manuallyCompletedCompanies.length + this.manuallyCompletedIndividuals.length + this.manuallyCompletedCommittees.length
    }
  },
  mounted (): void {
    const companyId = this.batch?.companyId ?? this.selectedCompany?.id
    if (companyId) {
      void this.loadHistoryIndividuals(companyId)
    }
  },
  watch: {
    showModal (val: boolean): void {
      if (!val) {
        this.$emit('close')
      }
    }
  },
  methods: {
    async loadHistoryIndividuals (companyId: string): Promise<void> {
      try {
        const response = await API.graphql({
          query: listFullCompanyIndividuals,
          variables: { companyId }
        })
        const items = response.data?.individualsByCompanyId?.items as PlatformCompanyIndividual[] | undefined
        if (!items) {
          this.historyIndividuals = []
          return
        }
        this.historyIndividuals = items
          .filter((item: PlatformCompanyIndividual) => !!item?.individual)
          .map((item: PlatformCompanyIndividual) => item.individual as PlatformIndividual)
      } catch (err) {
        console.error('Failed to load individuals for import history:', err)
        this.historyIndividuals = []
      }
    },
    storedIds (ids: Array<string | null> | null | undefined): string[] {
      return (ids ?? []).filter((id): id is string => !!id)
    },
    getCompanies (ids: string[]): Company[] {
      if (!this.selectedCompany || ids.length === 0) {
        return []
      }
      if (ids.includes(this.selectedCompany.id)) {
        return [this.selectedCompany]
      }
      return []
    },
    getIndividuals (ids: string[]): PlatformIndividual[] {
      return this.historyIndividuals.filter((i: PlatformIndividual) => ids.includes(i.id))
    },
    getCommittees (ids: string[]): PlatformCommittee[] {
      return this.committeesList.filter((c: PlatformCommittee) => ids.includes(c.id))
    },
    failedCommitteeMembers (committee: ImportBatchFailedCommitteeRow): string {
      if (!committee.members) {
        return '-'
      }
      return Array.isArray(committee.members)
        ? committee.members.join(', ')
        : committee.members
    },
    getCommitteeMembers (committee: PlatformCommittee): string {
      const members = committee.individuals?.items
      if (!members || members.length === 0) {
        return '-'
      }
      return members
        .map((ci) => {
          const individual = ci.individual ??
            this.historyIndividuals.find((i: PlatformIndividual) => i.id === ci.individualId)
          return individual?.preferredName
        })
        .filter(name => !!name)
        .join(', ') || '-'
    }
  }
})
</script>
